import {
  buildRejectSelectionSessionConfig,
  getDefaultRejectSelectionConfig,
  IRejectSelectionSessionConfig,
  normalizeRejectSelectionSessionConfigForReport,
  resolveRejectSelectionConfig,
} from './rejectConfig';
import { IGrain } from '../models';

export interface IProfileRejectConfigRecord {
  grainId?: string | number;
  profileId?: string | number;
  particleDistributionRejectKeys?: string[];
  rejectedGrainsRejectKeys?: string[];
  syncStatus?: string;
  rawJson?: {
    rejectSelectionConfig?: unknown;
    rejectionMapping?: Record<string, string>;
  };
}

export interface IGrainRejectionMappingRecord {
  grainId?: string | number;
  rejectionMapping?: Record<string, string>;
  rawJson?: { rejectionMapping?: Record<string, string> };
}

export interface IRejectConfigElectronApi {
  getGrainProfiles: (args: { grainId: string }) => Promise<IProfileRejectConfigRecord[]>;
  getGrains: () => Promise<IGrainRejectionMappingRecord[]>;
}

export function getRejectConfigStateKeys(params: {
  jobId?: string | number;
  grainId?: string | number;
  profileId?: string | number;
}): { mappingStateKey: string; profileStateKey: string } {
  const jobId = String(params.jobId ?? '');
  const grainId = String(params.grainId ?? '');
  const profileId = String(params.profileId ?? '');
  return {
    mappingStateKey: `${jobId}:${grainId}`,
    profileStateKey: `${jobId}:${grainId}:${profileId}`,
  };
}

export function resolveEffectiveRejectSelectionConfig(params: {
  grainId: string | number;
  profileId: string | number;
  rejectionMapping?: Record<string, string>;
  profile?: IProfileRejectConfigRecord;
  storedConfig?: unknown;
  jobConfig?: unknown;
}): IRejectSelectionSessionConfig {
  const grain: IGrain = {
    grainId: String(params.grainId),
    name: '',
    rejectionMapping: params.rejectionMapping,
  };

  if (params.profile) {
    const resolved = resolveRejectSelectionConfig({ grain, profile: params.profile });
    return buildRejectSelectionSessionConfig({
      grain,
      profileId: String(params.profileId),
      particleDistributionSelectedKeys: resolved.particleDistributionRejectKeys,
      rejectedGrainsSelectedKeys: resolved.rejectedGrainsRejectKeys,
      source: 'profile',
    });
  }

  const stored = normalizeRejectSelectionSessionConfigForReport(
    params.storedConfig as IRejectSelectionSessionConfig | undefined,
    grain
  );
  if (stored) return stored;
  const job = normalizeRejectSelectionSessionConfigForReport(
    params.jobConfig as IRejectSelectionSessionConfig | undefined,
    grain
  );
  if (job) return job;

  const defaults = getDefaultRejectSelectionConfig(grain);
  return buildRejectSelectionSessionConfig({
    grain,
    profileId: String(params.profileId),
    particleDistributionSelectedKeys: defaults.particleDistributionRejectKeys,
    rejectedGrainsSelectedKeys: defaults.rejectedGrainsRejectKeys,
    source: 'default',
  });
}

const matchesId = (left: unknown, right: unknown) => String(left ?? '') === String(right ?? '');

export async function loadCurrentProfileRejectConfig(params: {
  grainId: string | number;
  profileId: string | number;
  getLocalProfiles?: (args: { grainId: string }) => Promise<IProfileRejectConfigRecord[]>;
  getCloudProfiles?: (grainId: string) => Promise<IProfileRejectConfigRecord[]>;
}): Promise<IProfileRejectConfigRecord | undefined> {
  const grainId = String(params.grainId);
  const profileId = String(params.profileId);

  let localMatch: IProfileRejectConfigRecord | undefined;
  if (params.getLocalProfiles) {
    try {
      const profiles = await params.getLocalProfiles({ grainId });
      localMatch = Array.isArray(profiles)
        ? profiles.find((profile) => matchesId(profile.profileId, profileId))
        : undefined;
      if (localMatch?.syncStatus && localMatch.syncStatus !== 'synced') return localMatch;
    } catch {
      // Fall through to the cloud source when the local cache is unavailable.
    }
  }

  if (params.getCloudProfiles) {
    try {
      const profiles = await params.getCloudProfiles(grainId);
      const cloudMatch = Array.isArray(profiles)
        ? profiles.find((profile) => matchesId(profile.profileId, profileId))
        : undefined;
      return cloudMatch || localMatch;
    } catch {
      return localMatch;
    }
  }

  return localMatch;
}

export async function loadCurrentGrainRejectionMapping(params: {
  grainId: string | number;
  getLocalGrains?: () => Promise<IGrainRejectionMappingRecord[]>;
  getCloudMapping?: (grainId: string) => Promise<Record<string, string>>;
}): Promise<Record<string, string> | undefined> {
  const grainId = String(params.grainId);

  if (params.getLocalGrains) {
    try {
      const grains = await params.getLocalGrains();
      const grain = Array.isArray(grains)
        ? grains.find((candidate) => matchesId(candidate.grainId, grainId))
        : undefined;
      const mapping = grain?.rejectionMapping || grain?.rawJson?.rejectionMapping;
      if (mapping && Object.keys(mapping).length > 0) return mapping;
    } catch {
      // Fall through to the cloud source when the local cache is unavailable.
    }
  }

  if (params.getCloudMapping) {
    try {
      const mapping = await params.getCloudMapping(grainId);
      return mapping && Object.keys(mapping).length > 0 ? mapping : undefined;
    } catch {
      return undefined;
    }
  }

  return undefined;
}
